<script setup lang="ts">
import type { HouseDetails } from '~/types/house/types'

const { isMobile } = useBreakpoint()
const { t } = useI18n()

defineProps<{
  house: HouseDetails
  tag: 'aside' | 'header'
}>()
defineEmits<{ 'on-back': [] }>()

const showShareModal = defineModel<boolean>('shareModal', {
  default: () => false,
})
</script>

<template>
  <component
    :is="tag"
    class="sticky top-0 flex w-full bg-white sm:px-10 sm:pb-5"
  >
    <NuxtLink v-if="isMobile" class="text-gray-700" @click="$emit('on-back')">
      <BaseIcon name="arrowLeft" />
    </NuxtLink>
    <h2 v-else class="mb-0 text-[32px]">
      {{ house.name }}
    </h2>

    <div class="flex items-center gap-4 sm:gap-6">
      <WishlistToggle
        v-slot="slotProps"
        class="flex cursor-pointer items-center gap-2 hover:text-primary-700"
        :house-id="Number(house.id)"
        :house-name="house.name"
      >
        <HouseWishlistButton
          :is-active="slotProps.active"
          :size="isMobile ? 1.5 : 1"
        />
        <p class="m-0 hidden text-md font-medium sm:block">
          {{ t('addToFavourites') }}
        </p>
      </WishlistToggle>

      <button
        class="flex items-center gap-2 duration-300 hover:text-primary-700"
        :aria-label="$t('house.share.share')"
        type="button"
        @click="showShareModal = !showShareModal"
      >
        <BaseIcon
          aria-hidden="true"
          focusable="false"
          name="shareIos"
          :size="isMobile ? 1.5 : 1"
        />
        <p class="m-0 hidden text-md font-medium sm:block">
          {{ $t('house.share.share') }}
        </p>
      </button>
    </div>
  </component>
</template>

<style scoped>
aside {
  @apply flex-col gap-5 items-start;
}
aside > h2 {
  @apply mt-16;
}
header {
  @apply sticky top-0 z-fixed justify-between items-baseline gap-4 border-b border-b-gray-200 p-4 sm:justify-start sm:border-b-0 sm:p-0;
}
header > h2 {
  @apply text-right;
}
</style>

<i18n lang="yaml">
en:
  addToFavourites: Add to favourites
fr:
  addToFavourites: Coups de cœur
</i18n>
